// @ts-nocheck
import { ExitStrategy } from '@jo-v1/components';
import JoAdditionalQuestions from '@jo-v1/features/JoSingleProgramSubmission/JoAdditionalQuestions';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { LEAD_SUBMISSION_STEPS } from '@core/constants';
import useFormEvents from '@core/hooks/cohesion/useFormEvents';
import { selectFormContext, selectHeclidMap, selectProductCorrelationIdMap } from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import { selectSubmissions, hideLeadDelivery } from '@core/reducers/matchesSlice';
import { postGetLeadSchema } from '@core/services/leadDelivery';
import { getSchoolConfig } from '@core/services/schoolConfig';
import Diffy from '@core/utils/diffy';
import timer from '@core/utils/timer';

import JoPiiConfirmation from './JoPiiConfirmation';

const { PII_CONFIRMATION, EXIT_STRATEGY } = LEAD_SUBMISSION_STEPS;

const SingleProgramSubmission = ({ hit }) => {
  const [step, setStep] = useState<string>(undefined);
  const [tcpa, setTcpa] = useState(undefined);
  const [tcpaLoading, setTcpaLoading] = useState(true);
  const [tcpaErrorMsg, setTcpaErrorMsg] = useState<string | undefined>(undefined);
  const submissions = useSelector(selectSubmissions);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);
  const heclidMap = useSelector(selectHeclidMap);
  const inputs = useSelector(selectAllInputs);
  const [currentSchema, setCurrentSchema] = useState(undefined);
  const [prqSchema, setPrqSchema] = useState(undefined);
  const formContext = useSelector(selectFormContext);
  const [editMode, setEditMode] = useState(false);
  const [piiLeadErrors, setPiiLeadErrors] = useState<Array<{ field: string }>>([]);
  const diffy = new Diffy(inputs);
  const [prqLoading, setPrqLoading] = useState(true);

  const { formErrored } = useFormEvents({
    formContext,
    errorDetails: 'INVALID',
    errorMessage: 'Please provide your valid phone number.',
    errorType: 'Phone Validation',
  });

  const dispatch = useDispatch();

  // when currentProgram is set use getSchoolConfig to get tcpa
  useEffect(() => {
    if (hit) {
      const getTcpa = async () => {
        const schoolConfig = await getSchoolConfig(hit?.school?.id);

        if (schoolConfig.error) {
          setTcpaErrorMsg('We ran into an issue! Please try again.');
          await timer(2500);
          dispatch(hideLeadDelivery());
          return;
        }

        setTcpa({ ...schoolConfig, hit });
        setTcpaLoading(false);
      };
      getTcpa();
    }
  }, [hit]);

  useEffect(() => {
    // we only need to get the schema if a lead hasn't been submitted for this program
    if (!step) {
      const getSchema = async () => {
        setPrqLoading(true);
        try {
          const schema = await postGetLeadSchema({
            schoolId: hit?.school?.id,
            programId: hit?.program?.id,
            body: {
              lead: {
                location: {
                  postalCode: inputs?.zip?.value,
                },
              },
              trackingContext: {
                correlationId: productCorrelationIdMap[hit?.program?.id],
                publisher: new URLSearchParams(window.location.search).get('publisher') || window.location.host,
                heclid: heclidMap[hit?.program?.id],
                anonymousId: window._Cohesion.anonymousId,
                url: inputs?.sourceUrl?.value,
                experience: 'Voyager',
              },
            },
          });
          setCurrentSchema(schema);
          setPrqSchema(diffy.diffLdsSchema(schema, hit?.leadSource));
        } catch (error) {
          console.error('Failed to fetch PRQ schema', error);
          setPrqSchema(undefined);
        } finally {
          setPrqLoading(false);
        }
      };
      getSchema();
    }

    const programHasBeenSubmitted = submissions.find((submission) => submission.program.id === hit.program.id);
    if (programHasBeenSubmitted) {
      setStep(EXIT_STRATEGY);
    } else {
      setStep(PII_CONFIRMATION);
    }
  }, [hit.program.id]);

  if (!hit?.program?.id) return null;

  const renderMap = {
    PII_CONFIRMATION: (
      <JoPiiConfirmation
        tcpa={tcpa}
        tcpaLoading={tcpaLoading}
        tcpaErrorMsg={tcpaErrorMsg}
        step={step}
        setStep={setStep}
        currentProgram={hit}
        prqSchema={prqSchema}
        setPiiLeadErrors={setPiiLeadErrors}
        piiLeadErrors={piiLeadErrors}
        setEditMode={setEditMode}
        editMode={editMode}
        formErrored={formErrored}
        prqLoading={prqLoading}
      />
    ),
    ADDITIONAL_QUESTIONS: (
      <JoAdditionalQuestions
        currentProgram={hit}
        originalSchema={currentSchema}
        prqSchema={prqSchema}
        setStep={setStep}
        setPiiLeadErrors={setPiiLeadErrors}
        setEditMode={setEditMode}
        formErrored={formErrored}
        tcpa={tcpa}
        tcpaLoading={tcpaLoading}
        tcpaErrorMsg={tcpaErrorMsg}
      />
    ),
    EXIT_STRATEGY: <ExitStrategy />,
  };

  return <>{renderMap[step]}</>;
};

export default SingleProgramSubmission;
